/**
 * BlogPost.jsx — /blog/:slug
 * Individual blog post with markdown rendering, related posts, tool CTA.
 * Route: <Route path="/blog/:slug" element={<BlogPost/>}/>
 *
 * Dependencies: none (markdown rendered manually for simplicity)
 * Optional: npm install marked  — then replace renderMarkdown with marked()
 */
import React, { useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getPostBySlug, getRelatedPosts, CATEGORIES } from './blogPosts';

const T = {
  bg:     '#07080c',
  bg2:    '#0c0f18',
  bg3:    '#111520',
  surf:   '#161c2a',
  border: 'rgba(255,255,255,.07)',
  white:  '#dce6fa',
  muted:  'rgba(220,230,250,.45)',
  dim:    'rgba(220,230,250,.22)',
  cyan:   '#00c8f0',
  blue:   '#4d8fff',
  sans:   '"Bricolage Grotesque", sans-serif;',
  mono:   '"Bricolage Grotesque", sans-serif;',
};

// ── Simple Markdown renderer ─────────────────────────────────────
// Handles: h1-h3, bold, italic, code, tables, lists, links, paragraphs
// For production consider: npm install marked + use marked()
function renderMarkdown(md) {
  if (!md) return '';

  const lines  = md.trim().split('\n');
  const output = [];
  let i = 0;

  const escHtml = (t) => t.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

  const inline = (t) => t
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, `<code style="background:rgba(0,200,240,.12);border:1px solid rgba(0,200,240,.2);border-radius:4px;padding:1px 6px;font-family:JetBrains Mono,monospace;font-size:.88em;color:#00c8f0">$1</code>`)
    .replace(/\[(.+?)\]\((.+?)\)/g, `<a href="$2" style="color:#00c8f0;text-decoration:none;border-bottom:1px solid rgba(0,200,240,.3);transition:border-color .15s" onmouseover="this.style.borderColor='#00c8f0'" onmouseout="this.style.borderColor='rgba(0,200,240,.3)'">$1</a>`);

  while (i < lines.length) {
    const line = lines[i];

    // Code block
    if (line.startsWith('```')) {
      const lang  = line.slice(3).trim();
      const block = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        block.push(escHtml(lines[i]));
        i++;
      }
      output.push(`<pre style="background:#111520;border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:18px 20px;overflow-x:auto;margin:20px 0;font-size:12.5px;line-height:1.7"><code style="font-family:JetBrains Mono,monospace;color:#dce6fa">${block.join('\n')}</code></pre>`);
      i++;
      continue;
    }

    // Table
    if (line.startsWith('|')) {
      const rows  = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        if (!lines[i].match(/^[|\s-]+$/)) {
          const cells = lines[i].split('|').slice(1,-1).map(c => c.trim());
          rows.push(cells);
        }
        i++;
      }
      if (rows.length > 0) {
        const header = rows[0];
        const body   = rows.slice(1);
        output.push(`
          <div style="overflow-x:auto;margin:20px 0">
          <table style="width:100%;border-collapse:collapse;font-size:13.5px">
            <thead>
              <tr>${header.map(c=>`<th style="padding:10px 14px;background:#111520;color:#dce6fa;font-weight:700;text-align:left;border:1px solid rgba(255,255,255,.07)">${inline(c)}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${body.map((row,ri)=>`<tr>${row.map(c=>`<td style="padding:9px 14px;border:1px solid rgba(255,255,255,.06);background:${ri%2===0?'rgba(255,255,255,.02)':'rgba(0,0,0,.1)'};color:rgba(220,230,250,.75)">${inline(c)}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table></div>`);
      }
      continue;
    }

    // Headings
    if (line.startsWith('### ')) {
      output.push(`<h3 style="font-size:16px;font-weight:700;color:${T.white};margin:28px 0 10px;letter-spacing:-.01em">${inline(escHtml(line.slice(4)))}</h3>`);
      i++; continue;
    }
    if (line.startsWith('## ')) {
      output.push(`<h2 style="font-size:20px;font-weight:800;color:${T.white};margin:36px 0 12px;letter-spacing:-.02em;padding-top:16px;border-top:1px solid rgba(255,255,255,.06)">${inline(escHtml(line.slice(3)))}</h2>`);
      i++; continue;
    }
    if (line.startsWith('# ')) {
      output.push(`<h1 style="font-size:26px;font-weight:900;color:${T.white};margin:0 0 16px;letter-spacing:-.02em">${inline(escHtml(line.slice(2)))}</h1>`);
      i++; continue;
    }

    // Unordered list
    if (line.startsWith('- ') || line.startsWith('* ')) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
        items.push(`<li style="padding:3px 0;color:rgba(220,230,250,.75)">${inline(escHtml(lines[i].slice(2)))}</li>`);
        i++;
      }
      output.push(`<ul style="margin:14px 0 14px 22px;list-style:disc">${items.join('')}</ul>`);
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(`<li style="padding:3px 0;color:rgba(220,230,250,.75)">${inline(escHtml(lines[i].replace(/^\d+\.\s/,'')))}</li>`);
        i++;
      }
      output.push(`<ol style="margin:14px 0 14px 22px;list-style:decimal">${items.join('')}</ol>`);
      continue;
    }

    // Blank line
    if (line.trim() === '') {
      i++; continue;
    }

    // Paragraph
    output.push(`<p style="margin:0 0 16px;color:rgba(220,230,250,.78);line-height:1.8;font-size:15px">${inline(escHtml(line))}</p>`);
    i++;
  }

  return output.join('\n');
}

// ── Component ────────────────────────────────────────────────────
export default function BlogPost() {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const post       = getPostBySlug(slug);
  const related    = post ? getRelatedPosts(slug, post.tag) : [];
  const cat        = post ? CATEGORIES.find(c => c.id === post.tag) : null;
  const html       = useMemo(() => post ? renderMarkdown(post.content) : '', [post?.slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div style={{ background:T.bg, minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:T.sans }}>
        <div style={{ textAlign:'center' }}>
          <div style={{ fontSize:48, marginBottom:16 }}>📭</div>
          <h1 style={{ color:T.white, marginBottom:8 }}>Article not found</h1>
          <Link to="/blog" style={{ color:T.cyan, textDecoration:'none' }}>← Back to blog</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        .prose strong { color:#dce6fa; }
        .prose em     { color:rgba(220,230,250,.8); }
        @media (max-width:768px) {
          .blog-layout { grid-template-columns:1fr !important; }
          .blog-sidebar { display:none !important; }
        }
      `}</style>

      <div style={{ background:T.bg, minHeight:'100vh', fontFamily:T.sans, color:T.white }}>

        {/* Nav */}
        <nav style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'14px 40px', background:T.bg2,
          borderBottom:`1px solid ${T.border}`, position:'sticky', top:0, zIndex:10,
        }}>
          <Link to="/" style={{ fontSize:17, fontWeight:900, color:T.white, textDecoration:'none', letterSpacing:'-.02em' }}>
            Tylent<span style={{ color:T.cyan }}>ed</span>
          </Link>
          <div style={{ display:'flex', gap:20 }}>
            {[['/',  'Home'],['/tools','Tools'],['/blog','Blog']].map(([path, label]) => (
              <Link key={path} to={path} style={{ fontSize:13.5, color:T.muted, textDecoration:'none' }}>
                {label}
              </Link>
            ))}
          </div>
        </nav>

        {/* Breadcrumb */}
        <div style={{ maxWidth:920, margin:'0 auto', padding:'18px 24px 0', fontSize:12.5, color:T.dim }}>
          <Link to="/"    style={{ color:T.dim, textDecoration:'none' }}>Home</Link>
          {' › '}
          <Link to="/blog" style={{ color:T.dim, textDecoration:'none' }}>Blog</Link>
          {' › '}
          <span style={{ color:T.muted }}>{post.title}</span>
        </div>

        {/* Article */}
        <article style={{ maxWidth:920, margin:'0 auto', padding:'24px 24px 64px' }}>

          {/* Article header */}
          <header style={{ marginBottom:32 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
              <span style={{
                padding:'4px 11px', borderRadius:20, fontSize:12, fontWeight:700,
                background:`${cat?.color || T.cyan}18`,
                color: cat?.color || T.cyan,
              }}>
                {post.icon} {post.category}
              </span>
              <span style={{ fontSize:12, color:T.dim }}>{post.readTime} read</span>
              <span style={{ fontSize:12, color:T.dim }}>·</span>
              <span style={{ fontSize:12, color:T.dim }}>
                {new Date(post.date).toLocaleDateString('en-CA', { year:'numeric', month:'long', day:'numeric' })}
              </span>
            </div>

            <h1 style={{
              fontSize:'clamp(1.6rem,3.5vw,2.4rem)', fontWeight:900,
              color:T.white, letterSpacing:'-.03em', lineHeight:1.15, marginBottom:16,
            }}>
              {post.title}
            </h1>

            <p style={{ fontSize:16, color:T.muted, lineHeight:1.75, marginBottom:24 }}>
              {post.excerpt}
            </p>

            {/* Divider */}
            <div style={{ height:1, background:T.border }}/>
          </header>

          {/* Article body */}
          <div
            className="prose"
            dangerouslySetInnerHTML={{ __html: html }}
            style={{ fontSize:15, lineHeight:1.8 }}
          />

          {/* Article footer */}
          <div style={{
            marginTop:48, padding:'20px 24px', borderRadius:12,
            background:`${cat?.color || T.cyan}08`,
            border:`1px solid ${cat?.color || T.cyan}25`,
          }}>
            <div style={{ fontSize:13, fontWeight:700, color: cat?.color || T.cyan, marginBottom:6 }}>
              Try the free calculator →
            </div>
            <div style={{ fontSize:13.5, color:T.muted, marginBottom:14 }}>
              Everything in this article can be modelled with our free tools. No account required.
            </div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              <Link to="/tools/finance" style={{
                padding:'8px 18px', borderRadius:8,
                background:`${cat?.color || T.cyan}18`,
                border:`1px solid ${cat?.color || T.cyan}40`,
                color: cat?.color || T.cyan, fontSize:13, fontWeight:700, textDecoration:'none',
              }}>
                💰 Finance Calculators →
              </Link>
              <Link to="/tools" style={{
                padding:'8px 18px', borderRadius:8,
                border:`1px solid ${T.border}`, background:'transparent',
                color:T.muted, fontSize:13, textDecoration:'none',
              }}>
                🛠️ All Free Tools →
              </Link>
            </div>
          </div>

          {/* Back link */}
          <div style={{ marginTop:24 }}>
            <Link to="/blog" style={{
              fontSize:13.5, color:T.muted, textDecoration:'none',
              display:'inline-flex', alignItems:'center', gap:6,
            }}>
              ← Back to all articles
            </Link>
          </div>

          {/* Related posts */}
          {related.length > 0 && (
            <div style={{ marginTop:48 }}>
              <h2 style={{
                fontSize:18, fontWeight:800, color:T.white,
                letterSpacing:'-.02em', marginBottom:16,
              }}>
                Related articles
              </h2>
              <div style={{
                display:'grid',
                gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',
                gap:14,
              }}>
                {related.map(rp => {
                  const rCat = CATEGORIES.find(c => c.id === rp.tag);
                  return (
                    <Link key={rp.slug} to={`/blog/${rp.slug}`} style={{
                      display:'block', textDecoration:'none',
                      padding:'16px', borderRadius:10,
                      border:`1px solid ${T.border}`,
                      background:'rgba(255,255,255,.02)',
                      transition:'all .15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor=`${rCat?.color||T.cyan}40`; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor=T.border; }}
                    >
                      <div style={{ fontSize:11, color: rCat?.color||T.cyan, fontWeight:700, marginBottom:7 }}>
                        {rp.icon} {rp.category}
                      </div>
                      <div style={{ fontSize:14, fontWeight:700, color:T.white, lineHeight:1.4, marginBottom:8 }}>
                        {rp.title}
                      </div>
                      <div style={{ fontSize:12, color:T.dim }}>{rp.readTime} read</div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </article>

        {/* Footer */}
        <footer style={{
          borderTop:`1px solid ${T.border}`, padding:'24px 40px',
          background:T.bg2, textAlign:'center',
          fontSize:12, color:T.dim,
        }}>
          © 2026 Tylented Corp · Surrey, BC, Canada ·{' '}
          <Link to="/tools" style={{ color:T.muted, textDecoration:'none' }}>Free Tools</Link>
        </footer>
      </div>
    </>
  );
}
